import { ApiError, describeApiError, type ClaimChallenge, type RoomDirectory } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { ArrowLeft, Check } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useScreenViewed } from '../api';
import {
  claimFailureFor,
  codeSentNotice,
  methodLabel,
  NO_CODE_NOTICE,
  START_AGAIN_HINT,
  timeUntil,
} from '../claim';
import { useCompleteClaim, useStartClaim } from '../queries';
import { themeFor } from '../theme';
import { CenteredScreen } from './CenteredScreen';

/**
 * Connecting a sign-in to a player card (ADR 0010, Decision 3) — the whole flow, in three steps on
 * one screen: say where you can be reached, type the code, done.
 *
 * <b>What this component is really for is not typing a code into a box.</b> It is the one place in
 * the product where the response a person reads has been deliberately stripped of information, and
 * a screen is the easiest place to put it back. Every sentence it prints about a claim comes from
 * `claim.ts`; read the note at the top of that file before changing any of them.
 *
 * The room's own theme is deliberately not used. A claim is made *before* there is a room whose
 * portal this is — including the case where the room in the picker turns out not to hold a card
 * for this person at all — so the neutral palette is the honest one.
 */
export function ConnectCard({
  rooms,
  initialRoomId,
  onConnected,
  onCancel,
}: {
  rooms: readonly RoomDirectory[];
  initialRoomId?: string | null;
  /** The card is connected. Where that lands is the caller's decision. */
  onConnected: (roomId: string) => void;
  onCancel: () => void;
}) {
  const theme = themeFor(null);
  useScreenViewed('claim', null);
  const [roomId, setRoomId] = useState(initialRoomId ?? rooms[0]?.id ?? '');
  const [contact, setContact] = useState('');
  const [cardId, setCardId] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<ClaimChallenge | null>(null);
  const [connected, setConnected] = useState<{
    roomId: string;
    roomName: string;
    cardId: string;
    method: string;
  } | null>(null);

  const start = useStartClaim();
  const complete = useCompleteClaim();

  const room = rooms.find((r) => r.id === roomId) ?? null;
  const roomName = room?.name ?? 'that room';

  // A live countdown rather than "ten minutes", because what matters is how long is left rather
  // than how long it was: somebody who went to find their phone comes back to a screen that has
  // been sitting here.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!challenge) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [challenge]);
  const remaining = challenge ? timeUntil(challenge.expiresAt, now) : null;

  const startError = describeApiError(start.error);
  const completeError = describeApiError(complete.error);

  // Which controls to leave on screen beside the message. The message itself is always the
  // server's — including the attempts remaining, which is deliberately not counted here: the
  // server owns the cap, and a second count would be a second answer that goes wrong in the one
  // direction nobody would notice, telling somebody they have tries left when they have none.
  const failure = useMemo(
    () => (complete.error instanceof ApiError ? claimFailureFor(complete.error.code) : 'retry'),
    [complete.error],
  );

  const cardWanted = start.error instanceof ApiError && start.error.code === 'card_required';

  // Moved to rather than merely highlighted. `autoFocus` is a mount-time prop and the field is
  // already mounted when the refusal arrives, so it would do nothing — and this is the one refusal
  // whose whole content is "the next thing to type is here".
  const cardRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (cardWanted) cardRef.current?.focus();
  }, [cardWanted]);

  const panel = { background: theme.panel, border: `1px solid ${theme.border}` };
  const field = {
    background: theme.panelLight,
    color: theme.text,
    border: `1px solid ${theme.border}`,
  };
  const label = 'block text-xs font-semibold uppercase tracking-wider';

  const submitContact = (e: FormEvent) => {
    e.preventDefault();
    start.mutate(
      {
        roomId,
        // Email only, and so there is no channel picker: `sms` ships as a value the server refuses
        // with a reason of its own (`channel_not_enabled`), and a control whose second option is
        // always refused is worse than no control. When a sender for it exists, this is where the
        // picker goes.
        channel: 'email',
        contact: contact.trim(),
        cardId: cardId.trim() || null,
      },
      { onSuccess: (result) => setChallenge(result) },
    );
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    complete.mutate(
      { challengeId: challenge.challengeId, code: code.trim() },
      {
        onSuccess: (membership) =>
          setConnected({
            roomId: membership.roomId,
            roomName: membership.roomName,
            cardId: membership.cardId,
            method: membership.method,
          }),
      },
    );
  };

  const startOver = () => {
    setChallenge(null);
    setCode('');
    complete.reset();
    start.reset();
  };

  return (
    <CenteredScreen background={theme.bg} color={theme.text}>
      <div className="w-full max-w-lg rounded-2xl p-6" style={panel}>
        {connected ? (
          <div>
            <span
              className="flex size-10 items-center justify-center rounded-full"
              style={{ background: theme.accent, color: theme.bg }}
            >
              <Check className="size-5" />
            </span>
            <h1 className="mt-4 text-lg font-bold">Card connected</h1>
            <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
              Card {connected.cardId} at {connected.roomName} is yours. Proved by:{' '}
              {methodLabel(connected.method).toLowerCase()}.
            </p>
            <button
              type="button"
              onClick={() => onConnected(connected.roomId)}
              className="mt-6 w-full rounded-lg py-2.5 text-sm font-semibold"
              style={{ background: theme.accent, color: theme.bg }}
            >
              Open my portal
            </button>
          </div>
        ) : challenge ? (
          <form onSubmit={submitCode}>
            {/*
              A stop outcome takes the screen over rather than arriving underneath it. The two
              paragraphs below are about a code being on its way, and the alert says there is
              nothing a code can do — printed together they read as a contradiction, and the
              reader is left unsure whether one went out after all. The heading goes with them,
              because "Enter your code" is an instruction that cannot be followed here.
            */}
            <h1 className="text-lg font-bold">
              {failure === 'stop' ? 'Connect your player card' : 'Enter your code'}
            </h1>
            {failure === 'stop' ? null : (
              <>
                <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
                  {codeSentNotice(roomName, challenge.destinationMasked)}
                </p>
                <p className="mt-2 text-xs" style={{ color: theme.textMuted }}>
                  {NO_CODE_NOTICE}
                </p>
              </>
            )}

            {failure === 'retry' ? (
              <div className="mt-5">
                <label htmlFor="claim-code" className={label} style={{ color: theme.textMuted }}>
                  6-digit code
                </label>
                <input
                  id="claim-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  placeholder="000000"
                  className="mt-1.5 w-full rounded-lg px-3 py-2.5 text-center text-lg font-semibold tracking-[0.4em] outline-none"
                  style={field}
                />
                <p className="mt-1.5 text-xs" style={{ color: theme.textMuted }}>
                  {remaining
                    ? `Expires in ${remaining}.`
                    : 'This code has expired — start again for a new one.'}
                </p>
              </div>
            ) : null}

            <ErrorNotice message={completeError} className="mt-4" />

            <div className="mt-6 flex gap-2">
              {failure === 'retry' ? (
                <button
                  type="submit"
                  disabled={complete.isPending || code.trim().length === 0}
                  className="flex-1 rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50"
                  style={{ background: theme.accent, color: theme.bg }}
                >
                  {complete.isPending ? 'Checking…' : 'Connect my card'}
                </button>
              ) : null}

              {failure === 'stop' ? (
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 rounded-lg py-2.5 text-sm"
                  style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startOver}
                  className={`rounded-lg py-2.5 text-sm ${failure === 'restart' ? 'flex-1' : 'px-4'}`}
                  style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
                >
                  Start again
                </button>
              )}
            </div>

            {/* Same reason as the preamble: there is no "Start again" button on a stop, and
                offering a fresh code for a card no code can reach is the advice that sent
                somebody to the room's desk in the first place. */}
            {failure === 'stop' ? null : (
              <p className="mt-4 text-[11px]" style={{ color: theme.textMuted }}>
                {START_AGAIN_HINT}
              </p>
            )}
          </form>
        ) : (
          <form onSubmit={submitContact}>
            <button
              type="button"
              onClick={onCancel}
              className="mb-4 flex items-center gap-1.5 text-xs"
              style={{ color: theme.textMuted }}
            >
              <ArrowLeft className="size-3.5" /> Back
            </button>

            <h1 className="text-lg font-bold">Connect your player card</h1>
            <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
              A room sends a code to the email address it already holds for your card. Enter the
              address you gave them, and the code goes there.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="claim-room" className={label} style={{ color: theme.textMuted }}>
                  Room
                </label>
                <select
                  id="claim-room"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                  className="mt-1.5 w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                  style={field}
                >
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id} className="text-ink-900">
                      {r.name}
                    </option>
                  ))}
                </select>
                {rooms.length === 0 ? (
                  <p className="mt-1.5 text-xs" style={{ color: theme.textMuted }}>
                    No rooms to choose from yet.
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="claim-contact" className={label} style={{ color: theme.textMuted }}>
                  Email address
                </label>
                <input
                  id="claim-contact"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="mt-1.5 w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                  style={field}
                />
              </div>

              <div>
                <label htmlFor="claim-card" className={label} style={{ color: theme.textMuted }}>
                  Card number
                </label>
                <input
                  id="claim-card"
                  ref={cardRef}
                  value={cardId}
                  onChange={(e) => setCardId(e.target.value)}
                  placeholder="Only if the room asks for it"
                  className="mt-1.5 w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                  style={cardWanted ? { ...field, border: `1px solid ${theme.accent}` } : field}
                />
                <p className="mt-1.5 text-xs" style={{ color: theme.textMuted }}>
                  Some rooms always ask for it; otherwise it is needed only when one address covers
                  more than one card.
                </p>
              </div>
            </div>

            <ErrorNotice message={startError} className="mt-4" />

            <button
              type="submit"
              disabled={start.isPending || contact.trim().length === 0 || !roomId}
              className="mt-6 w-full rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50"
              style={{ background: theme.accent, color: theme.bg }}
            >
              {start.isPending ? 'Sending…' : 'Send me a code'}
            </button>
          </form>
        )}
      </div>
    </CenteredScreen>
  );
}
